import {
  AutocompleteInput,
  Create,
  SimpleForm,
  ReferenceInput,
  TextInput,
  NumberInput,
  useTranslate,
  SelectArrayInput,
  BooleanInput,
  Labeled,
  FormDataConsumer
} from "react-admin";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";

import * as User from "providers/user";
import { usePhotoUploadSubmitCreate } from "hooks/usePhotoUploadSubmitCreate";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import PhoneInput from "./PhoneInput";
import CustomDaysTimePicker from "./CustomDaysTimePicker";
import CustomHeaderActions from "./CustomHeaderActions";
import CustomImageInput from "./CustomImageInput";
import CustomImageInputWrapper from "./CustomImageInputWrapper";
import { amenitiesChoices, checkIfValidLocationOpenHours } from "utils/helpers";
import { Box } from "@mui/material";
import { useState } from "react";
import { CustomToolbar } from "./CustomToolbar";
import { timezoneChoices } from "../utils/timezone";
import { isPhoneValid } from "utils/isPhoneValid";
import { useNavigate } from "react-router-dom";
import { atUsernameRegex, facebookRegex } from "utils/validation";

export default function LocationCreate() {
  const { permissions, getUser } = User.useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { submit: submitWithUpload } = usePhotoUploadSubmitCreate({
    redirect: `/dashboard/Location`,
    resource: "Location",
    keys: ["cover_photo_url"],
    setIsLoading: setIsLoading
  });

  const submit = async (data: any) => {
    setIsLoading(true);
    const customOnSuccess = async () => {
      getUser();
      navigate("/dashboard/Location");
    };

    const payload = {
      ...data,
      company: { id: permissions.companyId }
    };

    submitWithUpload(payload, customOnSuccess);
  };

  const translate = useTranslate();

  return (
    <Create
      actions={<CustomHeaderActions title={translate("pagenames.location")} />}
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(validationSchema)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <CustomImageInputWrapper label={translate("locations.cover_photo")}>
          <CustomImageInput source="cover_photo_url" height="180px" />
        </CustomImageInputWrapper>
        <ReferenceInput
          source="menu.id"
          reference="Menu"
          filter={{ company_id: permissions.companyId }}
        >
          <AutocompleteInput
            optionText={(value) => value.name}
            label={translate("locations.menu")}
            fullWidth
          />
        </ReferenceInput>
        <TextInput
          source="name"
          label={translate("locations.name")}
          fullWidth
        />
        <TextInput
          source="nickname"
          label={translate("locations.nickname")}
          fullWidth
        />
        <BooleanInput
          source="visible_on_homepage"
          label={`${translate("locations.visible_on_homepage")} ${
            !permissions.isCompanyVerified
              ? translate("locations.visible_on_homepage_helper")
              : ""
          }`}
          fullWidth
          disabled={!permissions.isCompanyVerified}
          defaultValue={permissions.isCompanyVerified}
        />
        <SelectArrayInput
          label={translate("locations.amenities")}
          fullWidth
          source="amenities_enums"
          choices={amenitiesChoices}
        />
        <TextInput
          source="facebook_url"
          multiline
          fullWidth
          label="Facebook"
          helperText="https://facebook.com/pointment"
        />
        <TextInput
          source="instagram_url"
          multiline
          fullWidth
          label="Instagram"
          helperText="@pointment"
        />
        <TextInput
          source="twitter_url"
          multiline
          fullWidth
          label="Twitter"
          helperText="@pointment"
        />
        <TextInput
          source="website_url"
          multiline
          fullWidth
          label="Website"
          helperText="https://pointmentapp.com"
        />
        <TextInput
          source="description"
          label={translate("locations.description")}
          fullWidth
          multiline
        />
        <NumberInput
          source="multi_person"
          label={translate("locations.multi_person")}
          fullWidth
          min={0}
          max={3}
          step={1}
          defaultValue={0}
        />
        <PhoneInput source="phone_number" fullWidth />
        <BooleanInput
          source="send_sms"
          label={translate("locations.send_sms")}
          fullWidth
        />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData.send_sms && (
              <Labeled
                label={translate("locations.sms_phone_number")}
                fullWidth
              >
                <PhoneInput source="sms_phone_number" fullWidth {...rest} />
              </Labeled>
            )
          }
        </FormDataConsumer>
        <Box
          sx={{
            width: "100%",
            "& .MuiFilledInput-root": {
              borderRadius: "8px",
              border: "1px solid rgba(48, 47, 46, 0.06)",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              paddingBottom: "5px !important",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.06)"
              },
              "& fieldset": {
                border: "solid 1px transparent"
              },
              "&:hover fieldset": {
                border: "solid 1px transparent"
              },
              "&.Mui-focused fieldset": {
                border: "solid 1px #b8722a"
              }
            },
            "& .MuiFilledInput-input": {
              height: "18px",
              fontSize: "16px",
              borderRadius: "8px",
              fontFamily: "Inter",
              border: "none"
            }
          }}
        >
          <GooglePlacesAutocomplete />
        </Box>
        <AutocompleteInput
          source="timezone"
          choices={timezoneChoices}
          optionText={(choice: any) => choice.timezone}
          defaultValue="PST8PDT"
          label={translate("locations.timezone")}
          fullWidth
        />
        <h5>{translate("locations.hours")}</h5>
        <CustomDaysTimePicker />
      </SimpleForm>
    </Create>
  );
}

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required("Location name required."),
    menu: yup.object({
      id: yup.string().required("Menu selection required.")
    }),
    timezone: yup.string().required("Timezone required."),
    visible_on_homepage: yup.boolean().nullable(),
    caption: yup.string().nullable(),
    description: yup.string().nullable(),
    cover_photo_url: yup.object().nullable(),
    multi_person: yup.number().nullable(),
    phone_number: yup
      .string()
      .test("phone", "Invalid phone number", (value) => {
        return isPhoneValid(value);
      }),
    sms_phone_number: yup
      .string()
      .nullable()
      .when("send_sms", {
        is: true,
        then: (schema) =>
          schema
            .required("SMS phone number is required")
            .test("phone", "Invalid phone number", (value) => {
              if (!value) {
                return false;
              }
              return isPhoneValid(value);
            }),
        otherwise: (schema) => schema.notRequired()
      }),
    amenities: yup.array().of(yup.string()).nullable(),
    facebook_url: yup.string().matches(facebookRegex).nullable(),
    instagram_url: yup.string().matches(atUsernameRegex).nullable(),
    twitter_url: yup.string().matches(atUsernameRegex).nullable(),
    website_url: yup.string().url("This field must be a valid URL").nullable(),
    address: yup.string().nullable().required("Address is required"),
    city: yup.string().nullable().required("City is required"),
    state: yup.string().nullable().required("State is required"),
    country: yup.string().nullable().required("Country is required"),
    zipcode: yup.string().nullable().required("Zipcode is required"),
    monday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    monday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    tuesday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    tuesday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    wednesday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    wednesday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    thursday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    thursday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    friday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    friday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    saturday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    saturday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    sunday_hours_open: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      ),
    sunday_hours_close: yup
      .mixed()
      .test(
        "either-date-empty-string-undefined-or-null",
        "Must be either a valid date, an empty string, undefined or null",
        checkIfValidLocationOpenHours
      )
  })
  .required();
